'use client'
import '../../assets/css/heroSection.css'
import { updatePackageData } from '@/lib/reducers/packageReducer'
import { dispatch, useSelector } from '@/lib/store'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay, Pagination } from 'swiper/modules'
import Image from 'next/image'
import Link from 'next/link'
import {
  getBannerDimension,
  getBannerImageUrl,
  getProductUrl,
  hasBannerImage,
  isEmpty,
} from '@/utils/commonFunc'
import {
  CURRENT_SUBSIDIARY,
  DEFAULT_CITY_SLUG,
  PHADKE_SUBSIDIARY,
} from '@/utils/constants'
import { cmsRequest } from '@/helper/request'
import { GET_BANNER_API } from '@/helper/apiConstants'
import { useMediaQuery } from '@/hooks/use-media-query'
import HeroSectionSkeleton from '@/components/common/Skeleton/HeroSectionSkeleton'

// Aspect ratios of .hero_swiper .swiper-slide in heroSection.css, used when the
// CMS omits width/height (next/image throws on a NaN width).
const FALLBACK_SIZE = {
  mobile: { width: 377, height: 240 },
  desktop: { width: 1520, height: 368 },
}

type BannerTarget =
  | { kind: 'internal'; href: string }
  | { kind: 'external'; href: string }
  | { kind: 'action' }
  | { kind: 'static' }

/**
 * Resolves a CMS banner to what it should render as. Pure and hook-free so the
 * server and the hydration render always agree for a given citySlug.
 */
export const getBannerTarget = (item: any, citySlug: string): BannerTarget => {
  switch (item?.redirectionTypeName) {
    case 'INTERNAL_PRODUCT':
      // Both guards matter: getProductUrl builds /{type}/{city}/{id}/{slug}, and
      // an empty name collapses the path into a crawlable 404.
      if (isEmpty(item?.productId) || isEmpty(item?.productName))
        return { kind: 'action' }
      return {
        kind: 'internal',
        href: getProductUrl(
          {
            ...item,
            id: item.productId,
            name: item.productName,
            type: item.productType,
          },
          citySlug
        ),
      }
    case 'EXTERNAL':
      return isEmpty(item?.bannerRedirectionUrl)
        ? { kind: 'action' }
        : { kind: 'external', href: item.bannerRedirectionUrl }
    case 'NO_REDIRECTION':
      return { kind: 'static' }
    case 'INTERNAL_SEARCH':
    default:
      return { kind: 'action' }
  }
}

const HeroSection = ({
  isMobile,
  ssrBannerData,
}: {
  isMobile: boolean
  ssrBannerData?: Record<string, any[]>
}) => {
  const hasSsrBanners = useMemo(
    () =>
      Object.values(ssrBannerData || {}).some(
        (banners) => Array.isArray(banners) && banners.length > 0
      ),
    [ssrBannerData]
  )

  const [bannersBySubsidiary, setBannersBySubsidiary] = useState<
    Record<string, any[]>
  >(ssrBannerData || {})
  // Only ever goes true -> false, and only when SSR produced nothing.
  const [isFallbackLoading, setIsFallbackLoading] = useState(!hasSsrBanners)
  const [isHydrated, setIsHydrated] = useState(false)
  const didFetchRef = useRef(false)

  const currentCityObj = useSelector((state) => state.city?.currentCityObj)

  const { value: isDesktopLive, mounted } = useMediaQuery('(min-width: 640px)')
  const effectiveIsMobile = mounted ? !isDesktopLive : isMobile

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  useEffect(() => {
    if (hasSsrBanners || didFetchRef.current) return
    didFetchRef.current = true
    let cancelled = false

    const fetchBannerData = async () => {
      try {
        const apiResponse: any = await cmsRequest(GET_BANNER_API, 'post', {
          section: 'HERO',
          screen: 'HOME',
          bannerDocumentType: isMobile ? 'MOBILE_WEB' : 'DESKTOP',
        })
        if (!cancelled && apiResponse?.status === true) {
          setBannersBySubsidiary(apiResponse?.data?.bannersBySubsidiary || {})
        }
      } catch (error) {
        console.error('Error fetching banner data:', error)
      } finally {
        if (!cancelled) setIsFallbackLoading(false)
      }
    }

    fetchBannerData()
    return () => {
      cancelled = true
    }
  }, [hasSsrBanners, isMobile])

  // The persisted city only reaches Redux after mount, so both of these stay on
  // their SSR value until hydration completes.
  const subsidiaryKey =
    isHydrated && currentCityObj?.subsidiary === PHADKE_SUBSIDIARY
      ? PHADKE_SUBSIDIARY
      : CURRENT_SUBSIDIARY

  const citySlug =
    (isHydrated ? currentCityObj?.slugName : DEFAULT_CITY_SLUG) ||
    DEFAULT_CITY_SLUG

  const bannerArray = useMemo(() => {
    // A banner with no usable image would render <img src="">, so it is dropped
    // rather than shown as a blank slide. Filtering here also keeps the
    // length === 0 check below honest.
    const preferred = bannersBySubsidiary?.[subsidiaryKey]
    if (Array.isArray(preferred)) {
      const usable = preferred.filter(hasBannerImage)
      if (usable.length > 0) return usable
    }
    // Don't blank the hero just because a subsidiary bucket is missing.
    const fallback = bannersBySubsidiary?.[CURRENT_SUBSIDIARY]
    return Array.isArray(fallback) ? fallback.filter(hasBannerImage) : []
  }, [bannersBySubsidiary, subsidiaryKey])

  const openSearchModal = () =>
    dispatch(updatePackageData({ modalIsOpen: true }))

  if (!hasSsrBanners && isFallbackLoading) {
    return <HeroSectionSkeleton />
  }

  if (bannerArray.length === 0) return null

  return (
    <section
      className="container max-w-full py-3"
      aria-roledescription="carousel"
      aria-label="Offers and promotions"
    >
      <div className="rounded-[0.625rem] sm:rounded-[24px] overflow-hidden">
        <Swiper
          pagination={bannerArray.length > 1 ? { clickable: true } : false}
          autoplay={bannerArray.length > 1 ? { delay: 5000 } : false}
          loop={bannerArray.length > 1}
          slidesPerView={1}
          className="navigation_hidden hero_swiper"
          modules={[Autoplay, Pagination]}
          spaceBetween={48}
        >
          {bannerArray.map((item: any, index: number) => {
            const target = getBannerTarget(item, citySlug)
            // Never empty: an image with no alt inside a link leaves the link
            // with no accessible name and no anchor text for crawlers.
            const label =
              item?.bannerSubText?.trim() ||
              item?.productName?.trim() ||
              `Promotional banner ${index + 1}`
            const fallbackSize = effectiveIsMobile
              ? FALLBACK_SIZE.mobile
              : FALLBACK_SIZE.desktop
            // The slide carries the aspect-ratio, so any element between it and
            // the image has to fill the slide.
            const surface = 'block h-full w-full'

            const media = (
              <Image
                className="h-full w-full rounded-[0.625rem] sm:rounded-[24px]"
                // Non-empty: bannerArray is filtered by hasBannerImage above.
                src={getBannerImageUrl(item, effectiveIsMobile)}
                alt={label}
                width={getBannerDimension(item?.width, fallbackSize.width)}
                height={getBannerDimension(item?.height, fallbackSize.height)}
                quality={100}
                sizes="100vw"
                priority={index === 0}
              />
            )

            return (
              <SwiperSlide key={`hero_banner_${index}`}>
                {target.kind === 'internal' ? (
                  <Link href={target.href} className={surface} prefetch={false}>
                    {media}
                  </Link>
                ) : target.kind === 'external' ? (
                  <a
                    href={target.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={surface}
                  >
                    {media}
                    <span className="sr-only"> (opens in a new tab)</span>
                  </a>
                ) : target.kind === 'action' ? (
                  <button
                    type="button"
                    onClick={openSearchModal}
                    aria-haspopup="dialog"
                    className={`${surface} cursor-pointer appearance-none border-0 bg-transparent p-0 text-left`}
                  >
                    {media}
                  </button>
                ) : (
                  media
                )}
              </SwiperSlide>
            )
          })}
        </Swiper>
      </div>
    </section>
  )
}
export default HeroSection
